/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';

import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useTheme } from '../theme/ThemeContext';
import ThemedButton from '../components/common/ThemedButton';
import ThemedInput from '../components/common/ThemedInput';
import { inlineButton } from '../components/athlohive/EventFormFields';
import { readLookup } from '../components/athlohive/lookupOptions';
import { formatDateTime } from '../components/athlohive/formatDateTime';
import Pagination from '../components/Pagination';
import ProgressBarDialog from '../components/common/ProgressBar';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * Every event in the application, with a way into creating and editing one.
 *
 * The wizard was reachable only by URL before this: an admin could build an
 * event and then had no way back to it.
 */

/** How each status should read, and the colour it carries. */
const STATUS_STYLES = {
  DRAFT: { label: 'Draft', bg: '#f3f4f6', fg: '#4b5563' },
  PUBLISHED: { label: 'Published', bg: '#e0f2fe', fg: '#075985' },
  REGISTRATION_OPEN: { label: 'Registration open', bg: '#dcfce7', fg: '#166534' },
  REGISTRATION_CLOSED: { label: 'Registration closed', bg: '#fef3c7', fg: '#92400e' },
  COMPLETED: { label: 'Completed', bg: '#ede9fe', fg: '#5b21b6' },
  CANCELLED: { label: 'Cancelled', bg: '#fee2e2', fg: '#991b1b' },
};

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || { label: status || '—', bg: '#f3f4f6', fg: '#4b5563' };
  return (
    <span style={{
      background: style.bg, color: style.fg, borderRadius: 999,
      padding: '3px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {style.label}
    </span>
  );
}


const PAGE_SIZE = 20;

const EVENT_ORDERING = '-created_at';

export default function EventListPage() {
  const { id: applicationId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Search runs on the server now, so it must not fire on every keystroke.
  const [searchTerm, setSearchTerm] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(search.trim());
      setPage(1);          // a new search starts from its own first page
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: application, isLoading: applicationLoading } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/details/${applicationId}/`, { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error('Could not load this application.');
      return resp.json();
    },
    enabled: !!applicationId,
    staleTime: 60_000,
  });

  const organizationId = application?.organization?.id
    ?? application?.organization_id
    ?? (typeof application?.organization === 'number' ? application.organization : undefined);

  const events = useQuery({
    queryKey: ['athlohiveEvents', applicationId, organizationId, page, searchTerm],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        ordering: EVENT_ORDERING,
      });
      if (searchTerm) params.set('search', searchTerm);

      const resp = await apiHelper.apiCall(
        `/arcee/application/table/fetch/?${params.toString()}`,
        {
          method: 'POST',
          signal,
          body: JSON.stringify({
            table_name: 'event',
            organization_id: organizationId,
            application_id: Number(applicationId),
          }),
        },
      );
      if (!resp.ok) throw new Error('Could not load events.');
      const payload = await resp.json();
      return {
        rows: readLookup(payload).rows,
        // A bare array response has no count; fall back to what we were handed
        // so a single page still renders.
        count: typeof payload?.count === 'number'
          ? payload.count
          : readLookup(payload).rows.length,
      };
    },
    enabled: !!organizationId && !!applicationId,
    placeholderData: keepPreviousData,     // no flash of empty table while paging
    staleTime: 10_000,
  });

  // The server already returns the page in the order we asked for, so there is
  // nothing left to sort or filter here.
  const rows = events.data?.rows || [];
  const totalCount = events.data?.count || 0;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Deleting the last row of the final page (or a search that narrows the set)
  // can leave `page` past the end, which would render an empty table.
  //
  // Guarded on `events.data`: while a page is in flight the query has no data,
  // so `totalCount` reads 0 and `pageCount` collapses to 1 — which used to
  // clamp a perfectly valid page 2 straight back to page 1, silently.
  useEffect(() => {
    if (!events.data) return;
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount, events.data]);

  const cell = { padding: '12px 14px', borderBottom: '1px solid #eef0f3', fontSize: 14 };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 6, fontSize: 13, color: '#6b7280' }}>
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
        >
          {application?.name || 'Application'}
        </span>
        {' / Events'}
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap', marginBottom: 18,
      }}>
        <h2 style={{ color: theme.primary, margin: 0 }}>Events</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          {/* Organisers are what events belong to, so the way in sits here
              rather than as a second top-level button on the application page. */}
          <button
            type="button"
            onClick={() => navigate(`/dashboard/applications/${applicationId}/athlohive/organizations`)}
            style={{
              border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
              color: theme.primary, fontWeight: 600, fontSize: 14.5, textDecoration: 'underline',
            }}
          >
            Manage organisers
          </button>
          <ThemedButton
            type="button" style={inlineButton}
            onClick={() => navigate(`/dashboard/applications/${applicationId}/athlohive/events/create`)}
          >
            Create event
          </ThemedButton>
        </div>
      </div>

      <div style={{ maxWidth: 340, marginBottom: 16 }}>
        <ThemedInput
          placeholder="Search by title, location or status"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <ProgressBarDialog
        open={applicationLoading || events.isLoading}
        text_message="Loading events..."
        cancellable={false}
      />
      {events.isError && (
        <div style={{ color: '#d53b3b' }}>
          Events could not be loaded. Check that this account may read the event table.
        </div>
      )}

      {!applicationLoading && !events.isLoading && !events.isError && (
        rows.length === 0 ? (
          <div style={{
            border: '1px dashed #d1d5db', borderRadius: 12, padding: 32,
            textAlign: 'center', color: '#6b7280',
          }}>
            {searchTerm
              ? `No event matches “${searchTerm}”.`
              : 'No events yet. Create the first one to get started.'}
          </div>
        ) : (
          <div style={{
            border: '1px solid #e5e7eb', borderRadius: 12, overflowX: 'auto', background: '#fff',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                  <th style={{ ...cell, fontWeight: 700 }}>Title</th>
                  <th style={{ ...cell, fontWeight: 700 }}>Status</th>
                  <th style={{ ...cell, fontWeight: 700 }}>Starts</th>
                  <th style={{ ...cell, fontWeight: 700 }}>Location</th>
                  <th style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td style={cell}>
                      <div style={{ fontWeight: 600 }}>{row.title || '—'}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {row.number_of_activities || 0} activit
                        {row.number_of_activities === 1 ? 'y' : 'ies'}
                      </div>
                    </td>
                    <td style={cell}><StatusPill status={row.status} /></td>
                    <td style={cell}>{formatDateTime(row.start_datetime, row.timezone)}</td>
                    <td style={cell}>{row.location || '—'}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <ThemedButton
                        type="button" variant="outlined" style={inlineButton}
                        onClick={() => navigate(
                          `/dashboard/applications/${applicationId}/athlohive/events/${row.id}/edit`
                        )}
                      >
                        Edit
                      </ThemedButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {!events.isLoading && !events.isError && totalCount > 0 && (
        <>
          <div style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
            {`Showing ${(page - 1) * PAGE_SIZE + 1}\u2013${Math.min(page * PAGE_SIZE, totalCount)} of ${totalCount} event${totalCount === 1 ? '' : 's'}`}
          </div>
          {pageCount > 1 && (
            <Pagination
              totalPages={pageCount}
              currentPage={page}
              onPageChange={setPage}
              theme={theme}
            />
          )}
        </>
      )}
    </div>
  );
}
